/**
 * RitualGroupChatArea
 * Main chat area for ritual organizer group conversations.
 * Displays header with ritual info, messages, and input.
 */

import { useQueryClient } from "@tanstack/react-query"
import { ArrowLeft, ExternalLink, MoreVertical, Users } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import ritualityAiIcon from "@/assets/icons/rituality-ai.svg"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useRealtimeChannelEvent } from "@/contexts/RealtimeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import {
  useDeleteRitualGroupMessage,
  useEditRitualGroupMessage,
  useMarkRitualGroupAsRead,
  useRitualGroupChat,
  useRitualGroupMessages,
  useSendRitualGroupMessage,
} from "../../hooks/useRitualGroupChat"
import type { RitualGroupMessage } from "../../types/ritual-group-chat.types"
import { formatBookingSlot } from "../../utils/formatBookingSlot"
import { MessageInput } from "../shared/MessageInput"
import { OrganizersListDialog } from "./OrganizersListDialog"
import { RitualGroupMessageBubble } from "./RitualGroupMessageBubble"

function RitualGroupChatAreaInner({ id }: { id: string }) {
  const { t } = useTranslation("chats")
  const navigate = useNavigate()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const markAsReadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState("")
  const [editingMessage, setEditingMessage] = useState<RitualGroupMessage | null>(null)
  const [showOrganizers, setShowOrganizers] = useState(false)

  const user = useAuthStore(s => s.user)
  const queryClient = useQueryClient()
  const { data: chatData, isLoading: chatLoading } = useRitualGroupChat(id)
  const { data: messagesData, isLoading: messagesLoading } = useRitualGroupMessages(id)
  const sendMessage = useSendRitualGroupMessage()
  const editMessage = useEditRitualGroupMessage()
  const deleteMessage = useDeleteRitualGroupMessage()
  const markAsRead = useMarkRitualGroupAsRead()

  const messages = messagesData?.messages ?? []

  // Derived header state. Three flavors share this view: organizer chats
  // (no booking), ritual-booking expert chats, and coaching-only expert
  // chats. The badge + counterpart-cluster behavior is keyed off these.
  const isExpertChat = chatData?.bookingId != null
  const expert = isExpertChat ? chatData?.participants?.find(p => p.id !== user?.id) : undefined
  const headerBadgeLabel = isExpertChat
    ? chatData?.coachingSessionId != null
      ? "Coaching Session"
      : "Ritual Session"
    : "Organizers"

  const debouncedMarkAsRead = useCallback(
    (chatId: string) => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current)
      }
      markAsReadTimeoutRef.current = setTimeout(() => {
        markAsRead.mutate(chatId)
      }, 300)
    },
    [markAsRead],
  )

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (markAsReadTimeoutRef.current) {
        clearTimeout(markAsReadTimeoutRef.current)
      }
    }
  }, [])

  // Mark as read on open
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when conversation id changes
  useEffect(() => {
    if (id) {
      markAsRead.mutate(id)
    }
  }, [id])

  // Auto-scroll to bottom only when new messages arrive and user is near bottom
  const prevMessageCountRef = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const container = scrollContainerRef.current
      if (container) {
        const isNearBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight < 150
        if (isNearBottom) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
        }
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }
    }
    prevMessageCountRef.current = messages.length
  }, [messages.length])

  // WebSocket: new group message
  useRealtimeChannelEvent(
    `ritual-group:${id}`,
    "ritual-group:new_message",
    useCallback(
      (payload: unknown) => {
        const msg = payload as { ritualGroupConversationId: string }
        if (msg.ritualGroupConversationId === id) {
          queryClient
            .invalidateQueries({
              queryKey: ["ritual-group-messages", id],
            })
            .then(() => {
              debouncedMarkAsRead(id)
            })
        }
        queryClient.invalidateQueries({
          queryKey: ["ritual-group-chats"],
        })
      },
      [id, queryClient, debouncedMarkAsRead],
    ),
  )

  // WebSocket: message edited
  useRealtimeChannelEvent(
    `ritual-group:${id}`,
    "ritual-group:message_edited",
    useCallback(
      (payload: unknown) => {
        const msg = payload as { ritualGroupConversationId: string }
        if (msg.ritualGroupConversationId === id) {
          queryClient.invalidateQueries({
            queryKey: ["ritual-group-messages", id],
          })
        }
      },
      [id, queryClient],
    ),
  )

  // WebSocket: message deleted
  useRealtimeChannelEvent(
    `ritual-group:${id}`,
    "ritual-group:message_deleted",
    useCallback(
      (payload: unknown) => {
        const msg = payload as { ritualGroupConversationId: string }
        if (msg.ritualGroupConversationId === id) {
          queryClient.invalidateQueries({
            queryKey: ["ritual-group-messages", id],
          })
        }
        queryClient.invalidateQueries({
          queryKey: ["ritual-group-chats"],
        })
      },
      [id, queryClient],
    ),
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inputValue.trim()) return

    if (editingMessage) {
      editMessage.mutate(
        {
          groupChatId: id,
          messageId: editingMessage.id,
          content: inputValue.trim(),
        },
        {
          onSuccess: () => {
            setEditingMessage(null)
            setInputValue("")
          },
          onError: () => {
            toast.error("Failed to edit message")
          },
        },
      )
    } else {
      sendMessage.mutate(
        { groupChatId: id, content: inputValue.trim() },
        {
          onSuccess: () => {
            setInputValue("")
          },
          onError: () => {
            toast.error("Failed to send message")
          },
        },
      )
    }
  }

  const handleEdit = (message: RitualGroupMessage) => {
    setEditingMessage(message)
    setInputValue(message.content)
  }

  const handleCancelEdit = () => {
    setEditingMessage(null)
    setInputValue("")
  }

  const handleDelete = (messageId: string) => {
    deleteMessage.mutate(
      { groupChatId: id, messageId },
      {
        onError: () => {
          toast.error("Failed to delete message")
        },
      },
    )
  }

  if (chatLoading || messagesLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="size-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Header */}
      <div className="h-14 px-[30px] border-b border-border flex items-center gap-4">
        <Button variant="ghost" size="icon-xs" asChild className="md:hidden">
          <Link to="/chats">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>

        {/* Cover image — only wraps in a Link when a ritual exists to deep-
            link into. Coaching-only expert chats have no /rituals/:id page,
            so we render the image (or placeholder) directly to avoid the
            literal /rituals/null route. */}
        {chatData?.profilePhoto ? (
          chatData.ritualId ? (
            <Link
              to={`/rituals/${chatData.ritualId}`}
              className="size-[60px] rounded-lg shrink-0 overflow-hidden no-underline"
              aria-label="Open ritual"
            >
              <img
                src={chatData.profilePhoto}
                alt={chatData.name || "Ritual"}
                className="size-full object-cover"
              />
            </Link>
          ) : (
            <img
              src={chatData.profilePhoto}
              alt={chatData.name || "Coaching session"}
              className="size-[60px] rounded-lg shrink-0 overflow-hidden object-cover"
            />
          )
        ) : (
          <div className="size-[60px] rounded-lg bg-[var(--color-chat-border)] shrink-0 overflow-hidden" />
        )}

        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-foreground truncate">
            {chatData?.name || "Group Chat"}
          </h3>
          <div className="flex items-center gap-[10px] mt-1">
            <span className="flex items-center gap-[5px] text-[13px] font-semibold text-[var(--color-chat-badge)]">
              <img src={ritualityAiIcon} alt="" className="size-[14px]" />
              {headerBadgeLabel}
            </span>
            {expert && (
              <span className="flex items-center gap-2 text-[13px] text-foreground min-w-0">
                <Avatar
                  src={expert.profilePhoto || undefined}
                  alt={expert.name || ""}
                  fallback={expert.name || "?"}
                  size="sm"
                />
                <span className="font-semibold truncate">{expert.name}</span>
                {expert.username && (
                  <span className="text-muted-foreground truncate">@{expert.username}</span>
                )}
              </span>
            )}
          </div>
        </div>

        {/* Right side: participant summary + actions. Suppressed for expert
            chats because there's nothing to summarize beyond the expert
            already shown in the title row. */}
        <div className="flex items-center gap-3 shrink-0">
          {!isExpertChat && (
            <button
              type="button"
              onClick={() => setShowOrganizers(true)}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              aria-label="View organizers"
            >
              <span className="text-[13px] text-muted-foreground">
                {chatData?.participantCount || 0} organizers
              </span>
              <div className="flex -space-x-2">
                {chatData?.participants?.slice(0, 3).map(p => (
                  <Avatar
                    key={p.id}
                    src={p.profilePhoto || undefined}
                    alt={p.name || ""}
                    fallback={p.name || "?"}
                    size="sm"
                    className="border-2 border-[var(--color-brand-dark)]"
                  />
                ))}
              </div>
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More options"
                className="text-foreground hover:opacity-70 transition-opacity"
                title="More options"
              >
                <MoreVertical className="size-6" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {chatData?.ritualId && (
                <DropdownMenuItem onClick={() => navigate(`/rituals/${chatData.ritualId}`)}>
                  <ExternalLink />
                  View ritual details
                </DropdownMenuItem>
              )}
              {!isExpertChat && (
                <DropdownMenuItem onClick={() => setShowOrganizers(true)}>
                  <Users />
                  View organizers
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-[30px] py-4 space-y-3">
        {chatData?.bookingContext && (
          <p className="text-[13px] text-muted-foreground text-center py-4 max-w-[600px] mx-auto leading-relaxed">
            Your session with{" "}
            <span className="font-semibold text-foreground">
              {chatData.bookingContext.expertName || "your expert"}
            </span>{" "}
            has been booked for{" "}
            <span className="font-semibold text-foreground">
              {formatBookingSlot(chatData.bookingContext)}
            </span>
            . Use this chat to share details, ask questions, or prepare for the session together.
          </p>
        )}
        {messages.length === 0 && !chatData?.bookingContext && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-muted-foreground">
              Send a message to start the conversation
            </p>
          </div>
        )}
        <div className="max-w-[925px] mx-auto flex flex-col gap-5">
          {messages.map(message => (
            <RitualGroupMessageBubble
              key={message.id}
              message={message}
              isOwn={message.senderId === user?.id}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <MessageInput
        input={inputValue}
        onInputChange={setInputValue}
        onSubmit={handleSubmit}
        isLoading={sendMessage.isPending || editMessage.isPending}
        editMode={!!editingMessage}
        onCancelEdit={handleCancelEdit}
        editLabel={t("ritualGroupMessage.editing")}
        placeholder={t("ritualGroupMessage.placeholder")}
        autoFocus
      />

      <OrganizersListDialog
        open={showOrganizers}
        onOpenChange={setShowOrganizers}
        organizers={chatData?.participants ?? []}
      />
    </div>
  )
}

export function RitualGroupChatArea() {
  const { ritualGroupConversationId } = useParams<{ ritualGroupConversationId: string }>()
  if (!ritualGroupConversationId) return null
  return <RitualGroupChatAreaInner key={ritualGroupConversationId} id={ritualGroupConversationId} />
}
